/**
 * La Antena de «Armá tu nave» (encargo 117, decisión de Emmanuel del 19/10): cuenta los segundos con
 * el audio de la Audioteca SONANDO DE VERDAD y los suma al recorrido, en cualquier capítulo o libro.
 *
 * Se mide con el reloj de pared mientras el `<audio>` reproduce, por la velocidad: minutos de AUDIO
 * escuchado (Emmanuel, 20/10: acelerar no regala ni castiga — a 1,5× los dos minutos llegan a 1:20 de
 * reloj, a 0,75× a 2:40). No con la posición del capítulo: adelantar, retroceder o arrastrar la barra
 * no suma ni resta, y cambiar de capítulo no reinicia. No
 * cuenta mientras está en pausa, cargando (`readyState` bajo) o buscando (`seeking`). Sólo mientras el
 * recorrido está en curso y la Antena falta; manda lo medido cada ~5 s y al pausar/terminar.
 *
 * Capítulo corto (Emmanuel, 20/10): si el capítulo dura menos de dos minutos, terminarlo completa la
 * pieza — siempre que haya sonado de verdad casi entero (`CHAPTER_HEARD`), así saltar al final no
 * vale. Por eso además se lleva QUÉ TRAMOS de ese capítulo sonaron, y lo que falta en vivo es lo que
 * llegue primero: los dos minutos o el final del capítulo. Los tramos son de la línea de tiempo (lo
 * escuchado dos veces cuenta una) y se juntan aunque se escuche en varias veces: pausar, irse y
 * volver, o recargar (#575, 20/10). El capítulo se reconoce por la RUTA de su audio (libro, edición,
 * capítulo, idioma, nivel y voz), no por la URL entera: la firmada cambia en cada apertura. Se
 * guardan en el navegador y se borran al cambiar de cuenta.
 *
 * Lo monta una vez el `ListenProvider`, dueño del único `<audio>` de la app.
 */
import { createSessionCache } from '../../../lib/sessionCache.js';
import { ensureShip, peekShip, reportListen } from './useShip.js';

const TICK_MS = 1000;
/** Cada cuánto se manda lo medido (el servidor topea cada reporte en 30 s). */
const REPORT_SECONDS = 5;
/** Cuánto del capítulo tiene que haber sonado de verdad para que terminarlo cuente. */
const CHAPTER_HEARD = 0.85;

let pending = 0;
let last: number | null = null;
let sending = false;
/** Un capítulo terminado de verdad que todavía no llegó al servidor. */
let chapterDonePending = false;
/** Lo más que avanza el audio entre dos `timeupdate` seguidos sin que sea un salto (en segundos de reloj). */
const MAX_STEP_SECONDS = 2;
/** Capítulos cortos recordados (los últimos que sonaron). */
const MAX_CHAPTERS = 20;
const LS_KEY = 'orborix-ship-antena-heard-v1';

/** El `<audio>` medido. */
let current: HTMLAudioElement | null = null;
const subs = new Set<() => void>();

// ─── Lo que sonó de cada capítulo corto ─────────────────────────────────────────────────────────
/** Tramos `[desde, hasta]` (segundos de su línea de tiempo) que sonaron, por capítulo. */
type Spans = [number, number][];
let heard: Record<string, Spans> | null = null;
let heardDirty = false;
/** Posición del audio en el último `timeupdate` sonando (null = no venía sonando). */
let heardFrom: number | null = null;
// Marca de la cuenta: el AuthProvider vacía las cachés de sesión al entrar o salir; si la marca
// desaparece, lo escuchado es de otra persona y se borra.
const account = createSessionCache<true>();
account.set(true);

function heardMap(): Record<string, Spans> {
  if (account.get() === null) {
    account.set(true);
    heard = {};
    heardDirty = true;
    saveHeard();
  }
  if (heard) return heard;
  heard = {};
  try {
    const raw = localStorage.getItem(LS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') heard = parsed as Record<string, Spans>;
  } catch {
    /* localStorage no disponible / corrupto: se arranca de cero */
  }
  return heard;
}

function saveHeard(): void {
  if (!heardDirty || !heard) return;
  heardDirty = false;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(heard));
  } catch {
    /* best-effort: sin storage, lo escuchado vive hasta recargar */
  }
}

/** El capítulo por la ruta de su audio: la URL firmada cambia en cada apertura, la ruta no. */
function chapterOf(a: HTMLAudioElement): string | null {
  if (!a.currentSrc) return null;
  try {
    const u = new URL(a.currentSrc, window.location.href);
    return `${u.origin}${u.pathname}`;
  } catch {
    return a.currentSrc;
  }
}

/** Cuánto suman los tramos (más uno extra, si viene), sin contar dos veces lo que se pisa. */
function covered(spans: Spans, extra?: [number, number]): number {
  const all = extra ? [...spans, extra] : [...spans];
  all.sort((x, y) => x[0] - y[0]);
  let total = 0;
  let end = -Infinity;
  for (const [from, to] of all) {
    if (to <= end) continue;
    total += to - Math.max(from, end);
    end = to;
  }
  return total;
}

function addHeard(chapter: string, from: number, to: number): void {
  const map = heardMap();
  const spans = map[chapter] ?? [];
  spans.push([from, to]);
  // Juntar los tramos que se tocan (así no crece con cada `timeupdate`).
  spans.sort((x, y) => x[0] - y[0]);
  const merged: Spans = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    if (last && span[0] <= last[1] + 0.01) last[1] = Math.max(last[1], span[1]);
    else merged.push([span[0], span[1]]);
  }
  // El más reciente, al final: al pasarse del tope se olvidan los viejos.
  delete map[chapter];
  map[chapter] = merged;
  const keys = Object.keys(map);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_CHAPTERS))) delete map[k];
  heardDirty = true;
}

/** Cuánto sonó de verdad el capítulo cargado (más el tramo `extra`, si viene). */
function chapterHeard(a: HTMLAudioElement, extra?: [number, number]): number {
  const chapter = chapterOf(a);
  return chapter ? covered(heardMap()[chapter] ?? [], extra) : 0;
}

/** Lo escuchado que todavía no llegó al servidor (para que la barra avance segundo a segundo). */
export const pendingListenSeconds = (): number => pending;
export function subscribeListen(fn: () => void): () => void {
  subs.add(fn);
  return () => {
    subs.delete(fn);
  };
}

/** El capítulo cargado todavía puede completar la pieza al terminar (corto y escuchado casi entero). */
function chapterCanFinish(a: HTMLAudioElement, need: number): boolean {
  const d = a.duration;
  if (!Number.isFinite(d) || d <= 0 || d >= need) return false;
  return chapterHeard(a, [Math.min(a.currentTime, d), d]) >= CHAPTER_HEARD * d;
}

/** Un capítulo corto (menos que lo que pide la Antena): de esos se lleva qué sonó. */
function shortChapter(a: HTMLAudioElement): boolean {
  const need = peekShip()?.pieces.find((p) => p.id === 'antena')?.need;
  const d = a.duration;
  return need !== undefined && Number.isFinite(d) && d > 0 && d < need;
}

/**
 * Cuánto le falta a la Antena en vivo, en segundos de RELOJ a la velocidad actual (lo que va a tardar
 * de verdad): lo que llegue primero entre los dos minutos de audio (lo del servidor más lo medido que
 * todavía no llegó) y el final de un capítulo corto que se está escuchando de verdad.
 */
export function antenaRemaining(have: number, need: number): number {
  const a = current;
  const rate = a?.playbackRate || 1;
  let audioLeft = Math.max(0, need - have - pending);
  if (a && chapterCanFinish(a, need)) audioLeft = Math.min(audioLeft, Math.max(0, a.duration - a.currentTime));
  return audioLeft / rate;
}

function antenaMissing(): boolean {
  const s = peekShip();
  return s?.status === 'active' && s.pieces.some((p) => p.id === 'antena' && !p.done);
}

async function flush(): Promise<void> {
  if (sending || (pending < 1 && !chapterDonePending)) return;
  const seconds = Math.min(30, Math.round(pending));
  const chapterDone = chapterDonePending;
  sending = true;
  try {
    await reportListen(seconds, chapterDone);
    pending = Math.max(0, pending - seconds);
    if (chapterDone) chapterDonePending = false;
  } catch (err) {
    console.warn('[ship] no se pudo sumar lo escuchado', err);
  } finally {
    sending = false;
    subs.forEach((f) => f());
  }
  // Lo que llegó mientras se mandaba (p. ej. el final del capítulo) sale enseguida.
  if (chapterDonePending || pending >= REPORT_SECONDS) void flush();
}

export function attachShipListenMeter(audio: HTMLAudioElement): () => void {
  current = audio;
  const sounding = () => !audio.paused && !audio.seeking && audio.readyState >= 3;
  /** Suma a lo que sonó del capítulo el tramo desde el `timeupdate` anterior (si no fue un salto). */
  const noteHeard = () => {
    const from = heardFrom;
    heardFrom = sounding() ? audio.currentTime : null;
    if (from === null || heardFrom === null || !antenaMissing() || !shortChapter(audio)) return;
    const step = heardFrom - from;
    if (step <= 0 || step > MAX_STEP_SECONDS * (audio.playbackRate || 1)) return;
    const chapter = chapterOf(audio);
    if (chapter) addHeard(chapter, from, heardFrom);
  };
  const tick = () => {
    const now = performance.now();
    const prev = last;
    last = sounding() ? now : null;
    if (prev === null || !sounding()) return;
    if (!antenaMissing()) {
      pending = 0;
      return;
    }
    // Un tick perdido (pestaña en segundo plano) no suma de más: como mucho el doble del intervalo.
    // En tiempo del AUDIO (reloj × velocidad): lo que cuenta para la pieza.
    pending += (Math.min(2 * TICK_MS, now - prev) / 1000) * (audio.playbackRate || 1);
    saveHeard();
    subs.forEach((f) => f());
    if (pending >= REPORT_SECONDS) void flush();
  };
  const onStop = () => {
    tick();
    last = null;
    heardFrom = null;
    saveHeard();
    void flush();
  };
  const onEnded = () => {
    tick();
    last = null;
    heardFrom = null;
    const d = audio.duration;
    if (antenaMissing() && shortChapter(audio) && chapterHeard(audio) >= CHAPTER_HEARD * d) {
      chapterDonePending = true;
    }
    saveHeard();
    void flush();
  };
  const onPlay = () => {
    // Si se entró directo a la Audioteca, averiguar si hay recorrido (una vez).
    if (!peekShip()) void ensureShip();
    last = sounding() ? performance.now() : null;
    heardFrom = sounding() ? audio.currentTime : null;
  };
  const t = window.setInterval(tick, TICK_MS);
  audio.addEventListener('timeupdate', noteHeard);
  audio.addEventListener('playing', onPlay);
  audio.addEventListener('seeked', onPlay);
  audio.addEventListener('pause', onStop);
  audio.addEventListener('ended', onEnded);
  audio.addEventListener('seeking', onStop);
  audio.addEventListener('waiting', onStop);
  return () => {
    window.clearInterval(t);
    if (current === audio) current = null;
    audio.removeEventListener('timeupdate', noteHeard);
    audio.removeEventListener('playing', onPlay);
    audio.removeEventListener('seeked', onPlay);
    audio.removeEventListener('pause', onStop);
    audio.removeEventListener('ended', onEnded);
    audio.removeEventListener('seeking', onStop);
    audio.removeEventListener('waiting', onStop);
  };
}
